import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Plus,
  Loader2,
  Lock,
  Unlock,
  Eye,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

type PeriodType = 'MONTH' | 'QUARTER' | 'YEAR';
type PeriodStatus = 'OPEN' | 'CLOSED' | 'LOCKED';

export function FiscalPeriodsPage() {
  const { t, i18n } = useTranslation('fiscalPeriods');
  const { t: tCommon } = useTranslation('common');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';

  // Filters
  const [periodType, setPeriodType] = useState<PeriodType | ''>('');
  const [statusFilter, setStatusFilter] = useState<PeriodStatus | ''>('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [page, setPage] = useState(1);

  // Modal states
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateYear, setGenerateYear] = useState(new Date().getFullYear());
  const [generateType, setGenerateType] = useState<PeriodType>('MONTH');
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<{
    id: string;
    name: string;
    status: PeriodStatus;
  } | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Queries
  const { data, isLoading } = trpc.fiscalPeriod.list.useQuery({
    page,
    periodType: periodType || undefined,
    status: statusFilter || undefined,
    year: yearFilter,
  });

  const { data: periodDetail, isLoading: detailLoading } = trpc.fiscalPeriod.getSummary.useQuery(
    { id: selectedPeriod?.id ?? '' },
    { enabled: !!selectedPeriod && detailModalOpen }
  );

  const utils = trpc.useUtils();

  // Mutations
  const generateMutation = trpc.fiscalPeriod.generateYear.useMutation({
    onSuccess: () => {
      utils.fiscalPeriod.list.invalidate();
      setGenerateModalOpen(false);
    },
  });

  const closeMutation = trpc.fiscalPeriod.close.useMutation({
    onSuccess: () => {
      utils.fiscalPeriod.list.invalidate();
      setCloseModalOpen(false);
      setSelectedPeriod(null);
    },
  });

  const reopenMutation = trpc.fiscalPeriod.reopen.useMutation({
    onSuccess: () => {
      utils.fiscalPeriod.list.invalidate();
      setReopenModalOpen(false);
      setSelectedPeriod(null);
      setReopenReason('');
    },
  });

  const lockMutation = trpc.fiscalPeriod.lock.useMutation({
    onSuccess: () => {
      utils.fiscalPeriod.list.invalidate();
      setLockModalOpen(false);
      setSelectedPeriod(null);
    },
  });

  const periods = data?.periods ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0, totalPages: 0 };

  const statusColors: Record<PeriodStatus, string> = {
    OPEN: 'bg-green-100 text-green-700',
    CLOSED: 'bg-amber-100 text-amber-700',
    LOCKED: 'bg-red-100 text-red-700',
  };

  const statusIcons: Record<PeriodStatus, React.ReactNode> = {
    OPEN: <CheckCircle2 className="h-3.5 w-3.5" />,
    CLOSED: <XCircle className="h-3.5 w-3.5" />,
    LOCKED: <Lock className="h-3.5 w-3.5" />,
  };

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        <Button className="h-8" onClick={() => setGenerateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('generatePeriods')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <select
          value={periodType}
          onChange={(e) => setPeriodType(e.target.value as PeriodType | '')}
          className="h-8 border border-border bg-background px-4 py-2 focus:border-foreground focus:ring-1 focus:ring-foreground/20 focus:outline-none"
        >
          <option value="">{t('filters.allTypes')}</option>
          <option value="MONTH">{t('periodTypes.MONTH')}</option>
          <option value="QUARTER">{t('periodTypes.QUARTER')}</option>
          <option value="YEAR">{t('periodTypes.YEAR')}</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PeriodStatus | '')}
          className="h-8 border border-border bg-background px-4 py-2 focus:border-foreground focus:ring-1 focus:ring-foreground/20 focus:outline-none"
        >
          <option value="">{t('filters.allStatuses')}</option>
          <option value="OPEN">{t('status.OPEN')}</option>
          <option value="CLOSED">{t('status.CLOSED')}</option>
          <option value="LOCKED">{t('status.LOCKED')}</option>
        </select>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(parseInt(e.target.value))}
          className="h-8 border border-border bg-background px-4 py-2 focus:border-foreground focus:ring-1 focus:ring-foreground/20 focus:outline-none"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Periods Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : periods.length === 0 ? (
        <div className="text-center py-12 border border-border bg-card">
          <Calendar className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">{t('noPeriods')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t('noPeriodsHelp')}</p>
          <Button className="mt-4 h-8" onClick={() => setGenerateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('generatePeriods')}
          </Button>
        </div>
      ) : (
        <div className="border border-border bg-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">{t('columns.name')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium">{t('columns.type')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium">{t('columns.startDate')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium">{t('columns.endDate')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium">{t('columns.status')}</th>
                <th className="px-4 py-3 text-right text-sm font-medium">{t('columns.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {periods.map((period) => (
                <tr key={period.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{period.name}</td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground">
                      {t(`periodTypes.${period.periodType}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {new Date(period.startDate).toLocaleDateString(dateLocale)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {new Date(period.endDate).toLocaleDateString(dateLocale)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium',
                        statusColors[period.status as PeriodStatus]
                      )}
                    >
                      {statusIcons[period.status as PeriodStatus]}
                      {t(`status.${period.status}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPeriod({
                            id: period.id,
                            name: period.name,
                            status: period.status as PeriodStatus,
                          });
                          setDetailModalOpen(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {period.status === 'OPEN' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedPeriod({
                              id: period.id,
                              name: period.name,
                              status: period.status as PeriodStatus,
                            });
                            setCloseModalOpen(true);
                          }}
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          {t('actions.close')}
                        </Button>
                      )}
                      {period.status === 'CLOSED' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedPeriod({
                                id: period.id,
                                name: period.name,
                                status: period.status as PeriodStatus,
                              });
                              setReopenModalOpen(true);
                            }}
                          >
                            <Unlock className="mr-2 h-4 w-4" />
                            {t('actions.reopen')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setSelectedPeriod({
                                id: period.id,
                                name: period.name,
                                status: period.status as PeriodStatus,
                              });
                              setLockModalOpen(true);
                            }}
                          >
                            <Lock className="mr-2 h-4 w-4" />
                            {t('actions.lock')}
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {tCommon('previous')}
          </Button>
          <span className="px-4 py-2 text-sm">
            {tCommon('page')} {page} / {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
          >
            {tCommon('next')}
          </Button>
        </div>
      )}

      {/* Generate Modal */}
      {generateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-background p-6 shadow-lg">
            <h2 className="text-lg font-semibold mb-4">{t('generatePeriods')}</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t('selectYear')}</label>
                <select
                  value={generateYear}
                  onChange={(e) => setGenerateYear(parseInt(e.target.value))}
                  className="mt-1 w-full h-8 border border-border bg-background px-4 py-1 focus:border-foreground focus:ring-1 focus:ring-foreground/20 focus:outline-none"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('periodType')}</label>
                <select
                  value={generateType}
                  onChange={(e) => setGenerateType(e.target.value as PeriodType)}
                  className="mt-1 w-full h-8 border border-border bg-background px-4 py-1 focus:border-foreground focus:ring-1 focus:ring-foreground/20 focus:outline-none"
                >
                  <option value="MONTH">{t('periodTypes.MONTH')}</option>
                  <option value="QUARTER">{t('periodTypes.QUARTER')}</option>
                  <option value="YEAR">{t('periodTypes.YEAR')}</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" className="h-8" onClick={() => setGenerateModalOpen(false)}>
                {tCommon('cancel')}
              </Button>
              <Button
                className="h-8"
                onClick={() =>
                  generateMutation.mutate({ year: generateYear, periodType: generateType })
                }
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                {t('generatePeriods')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Close Modal */}
      {closeModalOpen && selectedPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-background p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-100 p-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold">{t('closeConfirm.title')}</h2>
            </div>
            <p className="text-muted-foreground">
              {t('closeConfirm.message', { name: selectedPeriod.name })}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" className="h-8" onClick={() => setCloseModalOpen(false)}>
                {tCommon('cancel')}
              </Button>
              <Button
                className="h-8"
                onClick={() => closeMutation.mutate({ id: selectedPeriod.id })}
                disabled={closeMutation.isPending}
              >
                {closeMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                {t('closeConfirm.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Modal */}
      {reopenModalOpen && selectedPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-background p-6 shadow-lg">
            <h2 className="text-lg font-semibold mb-4">{t('reopenConfirm.title')}</h2>
            <p className="text-muted-foreground mb-4">
              {t('reopenConfirm.message', { name: selectedPeriod.name })}
            </p>
            <div>
              <label className="text-sm font-medium">{t('reopenConfirm.reason')}</label>
              <textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder={t('reopenConfirm.reasonPlaceholder')}
                className="mt-1 w-full border border-border bg-background px-4 py-2 min-h-[80px]"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                className="h-8"
                onClick={() => {
                  setReopenModalOpen(false);
                  setReopenReason('');
                }}
              >
                {tCommon('cancel')}
              </Button>
              <Button
                className="h-8"
                onClick={() =>
                  reopenMutation.mutate({ id: selectedPeriod.id, reason: reopenReason })
                }
                disabled={reopenMutation.isPending || !reopenReason.trim()}
              >
                {reopenMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Unlock className="mr-2 h-4 w-4" />
                )}
                {t('reopenConfirm.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lock Modal */}
      {lockModalOpen && selectedPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-background p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-2">
                <Lock className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold">{t('lockConfirm.title')}</h2>
            </div>
            <p className="text-muted-foreground">
              {t('lockConfirm.message', { name: selectedPeriod.name })}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" className="h-8" onClick={() => setLockModalOpen(false)}>
                {tCommon('cancel')}
              </Button>
              <Button
                variant="destructive"
                className="h-8"
                onClick={() => lockMutation.mutate({ id: selectedPeriod.id })}
                disabled={lockMutation.isPending}
              >
                {lockMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Lock className="mr-2 h-4 w-4" />
                )}
                {t('lockConfirm.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModalOpen && selectedPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg bg-background p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{selectedPeriod.name}</h2>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium',
                  statusColors[selectedPeriod.status]
                )}
              >
                {statusIcons[selectedPeriod.status]}
                {t(`status.${selectedPeriod.status}`)}
              </span>
            </div>

            {detailLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : periodDetail ? (
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground">
                  {t('detail.summary')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-border p-4">
                    <p className="text-sm text-muted-foreground">{t('detail.journalEntries')}</p>
                    <p className="text-2xl font-bold mt-1">
                      {Object.values(periodDetail.summary.journalEntries).reduce(
                        (a, b) => a + b,
                        0
                      )}
                    </p>
                    <div className="mt-2 space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span>{t('detail.posted')}</span>
                        <span>{periodDetail.summary.journalEntries.POSTED ?? 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('detail.draft')}</span>
                        <span>{periodDetail.summary.journalEntries.DRAFT ?? 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="border border-border p-4">
                    <p className="text-sm text-muted-foreground">{t('detail.invoices')}</p>
                    <p className="text-2xl font-bold mt-1">{periodDetail.summary.invoices.count}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('detail.total')}:{' '}
                      {new Intl.NumberFormat(dateLocale, {
                        style: 'currency',
                        currency: 'TRY',
                      }).format(periodDetail.summary.invoices.total)}
                    </p>
                  </div>
                  <div className="border border-border p-4">
                    <p className="text-sm text-muted-foreground">{t('detail.payments')}</p>
                    <p className="text-2xl font-bold mt-1">{periodDetail.summary.payments.count}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('detail.total')}:{' '}
                      {new Intl.NumberFormat(dateLocale, {
                        style: 'currency',
                        currency: 'TRY',
                      }).format(periodDetail.summary.payments.total)}
                    </p>
                  </div>
                  <div className="border border-border p-4">
                    <p className="text-sm text-muted-foreground">{t('detail.expenses')}</p>
                    <p className="text-2xl font-bold mt-1">{periodDetail.summary.expenses.count}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('detail.total')}:{' '}
                      {new Intl.NumberFormat(dateLocale, {
                        style: 'currency',
                        currency: 'TRY',
                      }).format(periodDetail.summary.expenses.total)}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-6 flex justify-end">
              <Button variant="outline" className="h-8" onClick={() => setDetailModalOpen(false)}>
                {tCommon('close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
